/**
 * OE Condition service (`Hugo_TRLs`) — Drift + Traps / Roadblocks / Landmines.
 *
 * One record type covers Drift and TRLs (Tim, D06 / D06.1 / D03 §C.3–8; Beta-1 plan P2-01).
 * The collection name and the five original status keys are kept for n8n, indexes and seeds.
 * Full field reference: docs/beta/OE-CONDITION-MODEL.md. Summary of Hugo_TRLs/{id}:
 *
 *   // Identity
 *   conditionId        'YY-SSS-NNNN' human ID (P2-06), allocated in a Hugo_Counters transaction
 *   kind               'drift' | 'trl'
 *   type               'drift' | 'trap' | 'roadblock' | 'landmine'
 *   driftType          drift only: 'perception_of_risk' | 'how_work_is_done' | 'other'
 *   trapCategory       trap only: one of the 11 predominant traps (trlConstants.TRAP_CATEGORIES)
 *
 *   // Provenance
 *   organizationId, siteId, deptId, nwtId
 *   submittedBy, submittedByName, submittedAt         the entrant ("Entrant" in the UI, Q-26)
 *   enteredBy, enteredByName                          keyboard user (View As audit, P4-07)
 *
 *   // Content
 *   title, description, repsQuadrant (primary), repsQuadrants[]
 *
 *   // Severity at every hop (disagreement stays visible, P2-15)
 *   severityITM, severityFLS, severityDH: 'I'..'IV' | null
 *
 *   // Ownership (P2-04): recommended by the entrant, refined by L1/L2, assigned to a person by the DH
 *   ownerDeptId, ownerNwtId, ownerUid, ownerName, ownerHistory[{ ownerDeptId, ownerNwtId, ownerUid, ownerName, at, by, byName, note }]
 *
 *   // Workflow (trlConstants.CONDITION_STATUS)
 *   status             submitted → review_l1 → under_review → approved → resolution_pending → resolved
 *                      | rejected | withdrawn | cancelled
 *   statusHistory[]    { status, at, by, byName, actingFor?, note? }
 *   l1                 { openedAt, openedBy, approvedAt, approvedBy, approvedByName, severity, refAction, notes, returnedAt, returnNote } | null
 *   l2                 { approvedAt, approvedBy, approvedByName, severity, refActionApproved, revisedRefNotes, priority, dueDate, returnedAt, returnNote } | null
 *   resolution         { submittedAt, submittedBy, submittedByName, note, actions: [], l1ApprovedAt/By, l2ApprovedAt/By, returnedAt, returnNote } | null
 *   selfResolved       true when resolved straight from Pending Review ("found and fixed", P2-31)
 *   hiddenFor[]        uids who "cleaned house" (P2-13) — hidden for them, never deleted
 *   cancelledBy/At/Reason, previousStatus (admin override, P2-36)
 *   priority, businessImpact, dueDate (L2)
 *   refActions[]       { type: 'reverse'|'eliminate'|'fortify' (legacy 'reduce' = Reverse; migrated by P2-20), notes, addedBy, addedByName, addedAt }
 *
 *   // n8n
 *   aiAnalysis (WF4), patternMatch (WF5)
 *   createdAt, updatedAt, updatedBy
 */

import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  runTransaction,
  arrayUnion,
  Timestamp,
} from 'firebase/firestore';
import { collections, logActivity } from '../utils/firestore';
import { conditionKindOf } from '../assistants/drew/utils/trlConstants';
import { counterKey, formatConditionId, yearCode } from '../assistants/drew/utils/conditionIds';
import { refreshSnapshotsForCondition } from './oeSnapshotService';
import { isReadOnlyViewAs, ReadOnlyViewAsError } from './actingContext';

const TRL_COL = collections.trls;
const COUNTERS_COL = 'Hugo_Counters';
const TOOL_SLUG = 'drift-accumulation';

/* ------------------------------------------------------------------------ */
/* Shared helpers                                                            */
/* ------------------------------------------------------------------------ */

/** A statusHistory entry. `actingFor` = the member an admin was viewing as (P4-07). */
export function historyEntry(status, { by, byName, actingFor, note } = {}) {
  const entry = { status, at: Timestamp.now(), by: by || null, byName: byName || '' };
  if (actingFor) entry.actingFor = actingFor;
  if (note) entry.note = note;
  return entry;
}

/** Fire-and-forget activity log for the Recent Activity feed (P2-16). */
/** Transitions after which the REPS scores can change (P2-35): the condition starts, stops or moves. */
const SCORING_ACTIONS = new Set(['condition_l2_approved', 'condition_resolved', 'condition_cancelled', 'condition_owner_changed']);

function logConditionActivity(action, data, { by, actingFor, extra } = {}) {
  const touchesScore = SCORING_ACTIONS.has(action) || (action === 'condition_returned' && extra?.what === 'revision');
  if (touchesScore && data.organizationId) {
    // Fire-and-forget: recompute the org + site snapshot so the cards move with reality.
    refreshSnapshotsForCondition({ organizationId: data.organizationId, siteId: data.siteId || null }).catch((err) =>
      console.warn('Snapshot refresh after condition change failed:', err.message)
    );
  }
  const details = {
    trlId: data.id || null,
    conditionId: data.conditionId || null,
    title: data.title || '',
    type: data.type || null,
    status: data.status || null,
    organizationId: data.organizationId || null,
    siteId: data.siteId || null,
    deptId: data.deptId || null,
    nwtId: data.nwtId || null,
    ...(actingFor ? { actingFor } : {}),
    ...(extra || {}),
  };
  return logActivity(by || data.submittedBy || 'system', action, TOOL_SLUG, details);
}

/**
 * Allocate the next human ID for an organization + site + year in ONE transaction on
 * `Hugo_Counters/{orgId}_{SITE}_{YY}` (P2-06). Two conditions logged back-to-back at Brasília
 * get 26-BRA-0001 and 26-BRA-0002; Houston starts its own sequence.
 */
export async function allocateConditionId(organizationId, siteCode, at = new Date()) {
  const yy = yearCode(at);
  const code = String(siteCode || 'ORG').toUpperCase();
  const ref = doc(db, COUNTERS_COL, counterKey(organizationId || 'noorg', code, yy));
  const seq = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const next = (snap.exists() ? Number(snap.data().seq) || 0 : 0) + 1;
    tx.set(ref, { organizationId: organizationId || null, siteCode: code, year: yy, seq: next, updatedAt: Timestamp.now() }, { merge: true });
    return next;
  });
  return formatConditionId(yy, code, seq);
}

/* ------------------------------------------------------------------------ */
/* Create / Update                                                           */
/* ------------------------------------------------------------------------ */

/**
 * Create a new TRL entry submitted by an ITM.
 * @returns {Promise<string>} new TRL document ID
 */
export async function createTRL({
  organizationId,
  siteId,
  siteCode,
  deptId,
  nwtId,
  submittedBy,
  submittedByName,
  enteredBy,
  enteredByName,
  actingFor,
  type,
  driftType,
  trapCategory,
  title,
  description,
  repsQuadrant,
  repsQuadrants,
  severityITM,
  ownerDeptId,
  ownerNwtId,
  ownerUid = null, // Tim 26 Oct (D03 r18): the entrant may recommend a Site/Plant Manager or an Executive as owner
  ownerName = '',
  submitterRole,
}) {
  if (isReadOnlyViewAs()) throw new ReadOnlyViewAsError(); // P4-06
  if (!nwtId) throw new Error('nwtId is required for TRL creation');
  if (!submittedBy) throw new Error('submittedBy is required');
  if (!type || !title || !description) throw new Error('type, title, description are required');
  if (type === 'drift' && !driftType) throw new Error('Choose the type of drift.');
  if (type === 'trap' && !trapCategory) throw new Error('Choose the trap category.');
  const kind = conditionKindOf(type);

  // Support multiple impacted quadrants ("check all that apply") while keeping a
  // single primary `repsQuadrant` for everything that still reads one value.
  const quads = Array.isArray(repsQuadrants) && repsQuadrants.length
    ? repsQuadrants
    : (repsQuadrant ? [repsQuadrant] : ['safety']);

  const now = Timestamp.now();
  let conditionId = null;
  try {
    conditionId = await allocateConditionId(organizationId, siteCode, now);
  } catch (err) {
    // Never block an entry on the counter (rules not deployed yet, offline…): P2-20 backfills.
    console.warn('conditionId allocation failed — will be backfilled:', err?.message || err);
  }
  const docData = {
    conditionId,
    kind,
    driftType: type === 'drift' ? driftType : null,
    trapCategory: type === 'trap' ? trapCategory : null,
    ownerDeptId: ownerDeptId || null,
    ownerNwtId: ownerNwtId || null,
    ownerUid: ownerUid || null,
    ownerName: ownerUid ? ownerName || '' : '',
    ownerHistory: ownerDeptId || ownerUid
      ? [{ ownerDeptId: ownerDeptId || null, ownerNwtId: ownerNwtId || null, ownerUid: ownerUid || null, ownerName: ownerUid ? ownerName || '' : '', at: now, by: submittedBy, byName: submittedByName || '', note: 'Recommended by the entrant' }]
      : [],
    submitterRole: submitterRole || 'respondent', // review routing (P2-37)
    statusHistory: [historyEntry('submitted', { by: submittedBy, byName: submittedByName, actingFor })],
    l1: null,
    l2: null,
    resolution: null,
    selfResolved: false,
    hiddenFor: [],
    dueDate: null,
    organizationId: organizationId || null,
    siteId: siteId || null,
    deptId: deptId || null,
    nwtId,
    submittedBy,
    submittedByName: submittedByName || '',
    // Audit trail for "View as": who was physically at the keyboard. Equal to
    // submittedBy unless an admin entered this on the member's behalf.
    enteredBy: enteredBy || submittedBy,
    enteredByName: enteredByName || submittedByName || '',
    submittedAt: now,
    type,
    title,
    description,
    repsQuadrant: quads[0],
    repsQuadrants: quads,
    severityITM: severityITM || 'II',
    severityFLS: null,
    severityDH: null,
    status: 'submitted',
    priority: null,
    businessImpact: null,
    refActions: [],
    aiAnalysis: null,
    patternMatch: null,
    createdAt: now,
    updatedAt: now,
    updatedBy: submittedBy,
  };

  const ref = await addDoc(collection(db, TRL_COL), docData);
  logConditionActivity('condition_created', { id: ref.id, ...docData }, { by: submittedBy, actingFor });
  return ref.id;
}

/** Change the owner (department / NWT / person). Every change is appended to ownerHistory (P2-04). */
export async function setConditionOwner(trlId, { ownerDeptId, ownerNwtId = null, ownerUid = null, ownerName = '', note = '' }, { by, byName, actingFor } = {}) {
  const ref = doc(db, TRL_COL, trlId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('OE Condition not found.');
  const data = snap.data();
  const entry = { ownerDeptId: ownerDeptId || null, ownerNwtId, ownerUid, ownerName: ownerName || '', at: Timestamp.now(), by: by || null, byName: byName || '', note };
  if (actingFor) entry.actingFor = actingFor;
  await updateDoc(ref, {
    ownerDeptId: ownerDeptId || null,
    ownerNwtId,
    ownerUid,
    ownerName: ownerName || '',
    ownerHistory: [...(Array.isArray(data.ownerHistory) ? data.ownerHistory : []), entry],
    updatedAt: Timestamp.now(),
    updatedBy: by || data.updatedBy || null,
  });
  logConditionActivity('condition_owner_changed', { id: trlId, ...data }, { by, actingFor, extra: { ownerDeptId, ownerNwtId, ownerUid, ownerName } });
}

function history(existing, status, meta) {
  return [...(Array.isArray(existing.statusHistory) ? existing.statusHistory : []), historyEntry(status, meta)];
}

async function load(trlId) {
  if (isReadOnlyViewAs()) throw new ReadOnlyViewAsError(); // P4-06: every transition goes through here
  const ref = doc(db, TRL_COL, trlId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('OE Condition not found.');
  return { ref, data: snap.data() };
}

/* ------------------------------------------------------------------------ */
/* L1 review (P2-09 / P2-10)                                                 */
/* ------------------------------------------------------------------------ */

/**
 * The L1 reviewer opened the item: Pending Review → Review in Progress-L1 (idempotent), so the
 * entrant "knows where it's stuck" (C0820).
 */
export async function openL1(trlId, { by, byName, actingFor } = {}) {
  const { ref, data } = await load(trlId);
  if (data.status !== 'submitted') return false;
  await updateDoc(ref, {
    status: 'review_l1',
    l1: { ...(data.l1 || {}), openedAt: Timestamp.now(), openedBy: by || null, openedByName: byName || '' },
    statusHistory: history(data, 'review_l1', { by, byName, actingFor }),
    updatedAt: Timestamp.now(),
    updatedBy: by || data.updatedBy || null,
  });
  logConditionActivity('condition_l1_opened', { id: trlId, ...data, status: 'review_l1' }, { by, actingFor });
  return true;
}

/**
 * L1 approval: verify/modify severity, propose the REF action, adjust the recommended owner,
 * resolution notes → Review in Progress-L2 (or straight to Approved/Active on single-step routing,
 * P2-37). Also used to amend the review while it still sits at L2 ("Edit review").
 */
export async function flsApproveTRL(
  trlId,
  { severityFLS, refAction, notes, ownerDeptId, ownerNwtId, approvedBy, approvedByName, actingFor, singleStep = false, priority = null }
) {
  const { ref, data: existing } = await load(trlId);
  if (!['submitted', 'review_l1', 'under_review'].includes(existing.status)) {
    throw new Error('This item is no longer at L1 review.');
  }
  const amending = existing.status === 'under_review';
  const entry = refAction
    ? { type: refAction, notes: notes || '', addedBy: approvedBy, addedByName: approvedByName || '', addedAt: Timestamp.now(), level: 'l1' }
    : null;
  // One L1 proposal per condition (edits replace, they do not append)
  const others = (Array.isArray(existing.refActions) ? existing.refActions : []).filter((a) => a.level && a.level !== 'l1');
  const refActions = entry ? [...others, entry] : others;
  const nextStatus = amending ? 'under_review' : singleStep ? 'approved' : 'under_review';
  const ownerChanged = ownerDeptId !== undefined && (ownerDeptId !== existing.ownerDeptId || (ownerNwtId || null) !== (existing.ownerNwtId || null));
  const updates = {
    severityFLS: severityFLS || existing.severityFLS || existing.severityITM,
    status: nextStatus,
    refActions,
    l1: {
      ...(existing.l1 || {}),
      approvedAt: Timestamp.now(),
      approvedBy,
      approvedByName: approvedByName || '',
      severity: severityFLS || existing.severityFLS || existing.severityITM,
      refAction: refAction || null,
      notes: notes || '',
    },
    updatedAt: Timestamp.now(),
    updatedBy: approvedBy,
  };
  if (ownerChanged) {
    updates.ownerDeptId = ownerDeptId || null;
    updates.ownerNwtId = ownerNwtId || null;
    updates.ownerHistory = [
      ...(Array.isArray(existing.ownerHistory) ? existing.ownerHistory : []),
      { ownerDeptId: ownerDeptId || null, ownerNwtId: ownerNwtId || null, ownerUid: existing.ownerUid || null, ownerName: existing.ownerName || '', at: Timestamp.now(), by: approvedBy, byName: approvedByName || '', note: 'Changed at L1 review' },
    ];
  }
  if (!amending) updates.statusHistory = history(existing, nextStatus, { by: approvedBy, byName: approvedByName, actingFor });
  if (nextStatus === 'approved') {
    updates.severityDH = severityFLS || existing.severityITM;
    updates.priority = priority || existing.priority || 'P2';
    updates.l2 = { ...(existing.l2 || {}), approvedAt: Timestamp.now(), approvedBy, approvedByName: approvedByName || '', severity: updates.severityDH, priority: updates.priority, singleStep: true };
  }
  await updateDoc(ref, updates);
  logConditionActivity(nextStatus === 'approved' ? 'condition_l2_approved' : 'condition_l1_approved', { id: trlId, ...existing, status: nextStatus }, { by: approvedBy, actingFor });
}

/** Kept for callers that only amend the L1 review while it waits at L2. */
export async function updateFLSReview(trlId, { severityFLS, refAction, notes, ownerDeptId, ownerNwtId }, { reviewedBy, reviewedByName, actingFor } = {}) {
  const { data } = await load(trlId);
  if (data.status !== 'under_review') {
    throw new Error('This review is already confirmed by the Department Head and can no longer be edited.');
  }
  return flsApproveTRL(trlId, { severityFLS, refAction, notes, ownerDeptId, ownerNwtId, approvedBy: reviewedBy, approvedByName: reviewedByName, actingFor });
}

/** Not Approved at L1 (reason required by the UI). */
export async function flsRejectTRL(trlId, { reason, rejectedBy, rejectedByName, actingFor }) {
  const { ref, data: existing } = await load(trlId);
  await updateDoc(ref, {
    status: 'rejected',
    rejectionReason: reason || '',
    l1: { ...(existing.l1 || {}), rejectedAt: Timestamp.now(), rejectedBy: rejectedBy || null, rejectedByName: rejectedByName || '', notes: reason || '' },
    statusHistory: history(existing, 'rejected', { by: rejectedBy, byName: rejectedByName, actingFor, note: reason }),
    updatedAt: Timestamp.now(),
    updatedBy: rejectedBy,
  });
  logConditionActivity('condition_not_approved', { id: trlId, ...existing, status: 'rejected' }, { by: rejectedBy, actingFor });
}

/* ------------------------------------------------------------------------ */
/* L2 approval & prioritization (P2-11)                                      */
/* ------------------------------------------------------------------------ */

export const PRIORITIES = Object.freeze([
  { value: 'P0', label: 'P0 — Assign Due Date' },
  { value: 'P1', label: 'P1 — Immediate (this week)' },
  { value: 'P2', label: 'P2 — Next 30 days' },
  { value: 'P3', label: 'P3 — Backlog' },
]);

/**
 * L2 confirms severity, approves (or revises) the L1 REF proposal, assigns priority / due date and
 * the owner → Approved/Active. From here the owner department sees it in its list (P2-14).
 */
export async function dhConfirmTRL(
  trlId,
  {
    severityDH,
    priority,
    dueDate = null,
    refActionApproved = true,
    revisedRefNotes = '',
    ownerDeptId,
    ownerNwtId,
    ownerUid,
    ownerName,
    confirmedBy,
    confirmedByName,
    confirmedEnteredBy,
    actingFor,
    businessImpact = null,
  }
) {
  const { ref, data: existing } = await load(trlId);
  if (!['under_review', 'submitted', 'review_l1'].includes(existing.status)) {
    throw new Error('This item is not awaiting L2 approval.');
  }
  if (priority === 'P0' && !dueDate) throw new Error('P0 needs a due date.');
  const now = Timestamp.now();
  const updates = {
    severityDH: severityDH || existing.severityFLS || existing.severityITM,
    priority: priority || 'P2',
    dueDate: dueDate || null,
    businessImpact,
    status: 'approved',
    l2: {
      ...(existing.l2 || {}),
      approvedAt: now,
      approvedBy: confirmedBy,
      approvedByName: confirmedByName || '',
      severity: severityDH || existing.severityFLS || existing.severityITM,
      refActionApproved: Boolean(refActionApproved),
      revisedRefNotes: revisedRefNotes || '',
      priority: priority || 'P2',
      dueDate: dueDate || null,
    },
    statusHistory: history(existing, 'approved', { by: confirmedBy, byName: confirmedByName, actingFor }),
    confirmedEnteredBy: confirmedEnteredBy || confirmedBy || null,
    updatedAt: now,
    updatedBy: confirmedBy,
  };
  if (revisedRefNotes) {
    updates.refActions = [
      ...(Array.isArray(existing.refActions) ? existing.refActions : []),
      { type: existing.l1?.refAction || null, notes: revisedRefNotes, addedBy: confirmedBy, addedByName: confirmedByName || '', addedAt: now, level: 'l2' },
    ];
  }
  const ownerChanged =
    (ownerDeptId !== undefined && ownerDeptId !== existing.ownerDeptId) ||
    (ownerNwtId !== undefined && (ownerNwtId || null) !== (existing.ownerNwtId || null)) ||
    (ownerUid !== undefined && (ownerUid || null) !== (existing.ownerUid || null));
  if (ownerChanged) {
    updates.ownerDeptId = ownerDeptId !== undefined ? ownerDeptId || null : existing.ownerDeptId || null;
    updates.ownerNwtId = ownerNwtId !== undefined ? ownerNwtId || null : existing.ownerNwtId || null;
    updates.ownerUid = ownerUid !== undefined ? ownerUid || null : existing.ownerUid || null;
    updates.ownerName = ownerUid !== undefined ? ownerName || '' : existing.ownerName || '';
    updates.ownerHistory = [
      ...(Array.isArray(existing.ownerHistory) ? existing.ownerHistory : []),
      { ownerDeptId: updates.ownerDeptId, ownerNwtId: updates.ownerNwtId, ownerUid: updates.ownerUid, ownerName: updates.ownerName, at: now, by: confirmedBy, byName: confirmedByName || '', note: 'Set at L2 approval' },
    ];
  }
  await updateDoc(ref, updates);
  logConditionActivity('condition_l2_approved', { id: trlId, ...existing, status: 'approved' }, { by: confirmedBy, actingFor });
}

/** L2 sends the item back to L1 with a note (the FLS must see the revisions — D03 §C.7.2). */
export async function dhReturnToL1(trlId, { note, by, byName, actingFor } = {}) {
  const { ref, data: existing } = await load(trlId);
  if (existing.status !== 'under_review') throw new Error('Only items awaiting L2 approval can be returned to L1.');
  await updateDoc(ref, {
    status: 'review_l1',
    l2: { ...(existing.l2 || {}), returnedAt: Timestamp.now(), returnedBy: by || null, returnNote: note || '' },
    l1: { ...(existing.l1 || {}), returnedAt: Timestamp.now(), returnNote: note || '' },
    statusHistory: history(existing, 'review_l1', { by, byName, actingFor, note: note ? `Returned to L1: ${note}` : 'Returned to L1' }),
    updatedAt: Timestamp.now(),
    updatedBy: by || existing.updatedBy || null,
  });
  logConditionActivity('condition_returned', { id: trlId, ...existing, status: 'review_l1' }, { by, actingFor, extra: { note } });
}

/** Not Approved at L2. */
export async function dhRejectTRL(trlId, { reason, by, byName, actingFor } = {}) {
  const { ref, data: existing } = await load(trlId);
  await updateDoc(ref, {
    status: 'rejected',
    rejectionReason: reason || '',
    l2: { ...(existing.l2 || {}), rejectedAt: Timestamp.now(), rejectedBy: by || null, rejectedByName: byName || '', notes: reason || '' },
    statusHistory: history(existing, 'rejected', { by, byName, actingFor, note: reason }),
    updatedAt: Timestamp.now(),
    updatedBy: by || existing.updatedBy || null,
  });
  logConditionActivity('condition_not_approved', { id: trlId, ...existing, status: 'rejected' }, { by, actingFor });
}

/* ------------------------------------------------------------------------ */
/* Resolution chain (P2-12), cleaning house (P2-13), override (P2-36)       */
/* ------------------------------------------------------------------------ */

/**
 * The owner (or the entrant while Pending Review, or an admin) says it is fixed. From Pending
 * Review this closes the record directly ("found and fixed", `selfResolved`); otherwise it becomes
 * Resolution Pending Approval and goes L1 → L2.
 */
export async function submitResolution(trlId, { note, actions = [], by, byName, actingFor, singleStep = false } = {}) {
  if (!note || !String(note).trim()) throw new Error('Describe what was done to resolve the condition.');
  const { ref, data: existing } = await load(trlId);
  if (!['submitted', 'review_l1', 'under_review', 'approved'].includes(existing.status)) {
    throw new Error('This condition cannot be resolved from its current status.');
  }
  const now = Timestamp.now();
  const direct = existing.status === 'submitted';
  const resolution = {
    ...(existing.resolution || {}),
    submittedAt: now,
    submittedBy: by || null,
    submittedByName: byName || '',
    note: String(note).trim(),
    actions: Array.isArray(actions) ? actions : [],
    l1ApprovedAt: null,
    l1ApprovedBy: null,
    l2ApprovedAt: null,
    l2ApprovedBy: null,
    returnedAt: null,
    returnNote: '',
  };
  const nextStatus = direct ? 'resolved' : 'resolution_pending';
  const updates = {
    status: nextStatus,
    previousStatus: existing.status,
    resolution,
    statusHistory: history(existing, nextStatus, { by, byName, actingFor, note: resolution.note }),
    updatedAt: now,
    updatedBy: by || existing.updatedBy || null,
  };
  if (direct) {
    Object.assign(updates, { selfResolved: true, resolvedAt: now, resolvedBy: by || null, resolvedByName: byName || '', resolutionNote: resolution.note });
  }
  await updateDoc(ref, updates);
  logConditionActivity(direct ? 'condition_resolved' : 'condition_resolution_submitted', { id: trlId, ...existing, status: nextStatus }, { by, actingFor });
  return nextStatus;
}

/** L1 / L2 sign-off on a submitted resolution; the last required sign-off closes the record. */
export async function approveResolution(trlId, { level, by, byName, actingFor, singleStep = false } = {}) {
  const { ref, data: existing } = await load(trlId);
  if (existing.status !== 'resolution_pending') throw new Error('No resolution is awaiting approval.');
  const now = Timestamp.now();
  const resolution = { ...(existing.resolution || {}) };
  if (level === 'l1') {
    resolution.l1ApprovedAt = now;
    resolution.l1ApprovedBy = by || null;
    resolution.l1ApprovedByName = byName || '';
  } else {
    if (!resolution.l1ApprovedAt) throw new Error('The L1 sign-off comes first.');
    resolution.l2ApprovedAt = now;
    resolution.l2ApprovedBy = by || null;
    resolution.l2ApprovedByName = byName || '';
  }
  const done = level === 'l2' || singleStep;
  const updates = { resolution, updatedAt: now, updatedBy: by || existing.updatedBy || null };
  if (done) {
    Object.assign(updates, {
      status: 'resolved',
      resolvedAt: now,
      resolvedBy: resolution.submittedBy || by || null,
      resolvedByName: resolution.submittedByName || byName || '',
      resolutionNote: resolution.note || '',
      statusHistory: history(existing, 'resolved', { by, byName, actingFor, note: `Resolution approved (${level.toUpperCase()})` }),
    });
  }
  await updateDoc(ref, updates);
  logConditionActivity(done ? 'condition_resolved' : 'condition_resolution_submitted', { id: trlId, ...existing, status: done ? 'resolved' : existing.status }, { by, actingFor, extra: { level } });
  return done;
}

/** L1 or L2 returns a resolution to the owner with a note → back to Approved/Active. */
export async function returnResolution(trlId, { note, by, byName, actingFor } = {}) {
  const { ref, data: existing } = await load(trlId);
  if (existing.status !== 'resolution_pending') throw new Error('No resolution is awaiting approval.');
  const now = Timestamp.now();
  const resolution = { ...(existing.resolution || {}), returnedAt: now, returnedBy: by || null, returnNote: note || '', l1ApprovedAt: null, l2ApprovedAt: null };
  const back = existing.previousStatus && existing.previousStatus !== 'submitted' ? existing.previousStatus : 'approved';
  await updateDoc(ref, {
    status: back,
    resolution,
    statusHistory: history(existing, back, { by, byName, actingFor, note: note ? `Resolution returned: ${note}` : 'Resolution returned' }),
    updatedAt: now,
    updatedBy: by || existing.updatedBy || null,
  });
  logConditionActivity('condition_returned', { id: trlId, ...existing, status: back }, { by, actingFor, extra: { note, what: 'resolution' } });
}

/** Cleaning house (P2-13): hide a closed item from MY lists; the record stays. */
export async function hideConditionFor(trlId, uid) {
  if (!uid) return;
  await updateDoc(doc(db, TRL_COL, trlId), { hiddenFor: arrayUnion(uid), updatedAt: Timestamp.now() });
}

/** Administrator override (P2-36): cancel at any status, reason required, record kept. */
export async function cancelCondition(trlId, { reason, by, byName, actingFor } = {}) {
  if (!reason || !String(reason).trim()) throw new Error('A reason is required to cancel an OE Condition.');
  const { ref, data: existing } = await load(trlId);
  await updateDoc(ref, {
    status: 'cancelled',
    previousStatus: existing.status,
    cancelledAt: Timestamp.now(),
    cancelledBy: by || null,
    cancelledByName: byName || '',
    cancelReason: String(reason).trim(),
    statusHistory: history(existing, 'cancelled', { by, byName, actingFor, note: String(reason).trim() }),
    updatedAt: Timestamp.now(),
    updatedBy: by || existing.updatedBy || null,
  });
  logConditionActivity('condition_cancelled', { id: trlId, ...existing, status: 'cancelled' }, { by, actingFor, extra: { reason } });
}

/**
 * Self-correction (P2-36): reopen a decision to the hop that owns it.
 *   toStatus 'review_l1'   — back to L1 (undo an L1 approval / Not Approved, or an admin reset)
 *   toStatus 'under_review'— back to L2 (a DH revising their Approved/Active or Not Approved)
 */
export async function reviseDecision(trlId, { toStatus, note, by, byName, actingFor } = {}) {
  if (!['review_l1', 'under_review'].includes(toStatus)) throw new Error('Unknown revision target.');
  const { ref, data: existing } = await load(trlId);
  const now = Timestamp.now();
  const updates = {
    status: toStatus,
    previousStatus: existing.status,
    rejectionReason: '',
    statusHistory: history(existing, toStatus, { by, byName, actingFor, note: note ? `Decision revised: ${note}` : 'Decision revised' }),
    updatedAt: now,
    updatedBy: by || existing.updatedBy || null,
  };
  if (toStatus === 'under_review') updates.l2 = { ...(existing.l2 || {}), revisedAt: now, revisedBy: by || null, revisedNote: note || '', rejectedAt: null };
  if (toStatus === 'review_l1') updates.l1 = { ...(existing.l1 || {}), revisedAt: now, revisedBy: by || null, revisedNote: note || '', rejectedAt: null };
  await updateDoc(ref, updates);
  logConditionActivity('condition_returned', { id: trlId, ...existing, status: toStatus }, { by, actingFor, extra: { note, what: 'revision' } });
}

/* ------------------------------------------------------------------------ */
/* ITM self-service — edit / withdraw own submission before review            */
/* ------------------------------------------------------------------------ */

/** Editable fields an ITM may change on their own pre-review submission. */
const ITM_EDITABLE_FIELDS = ['type', 'kind', 'driftType', 'trapCategory', 'title', 'description', 'repsQuadrant', 'repsQuadrants', 'severityITM', 'ownerDeptId', 'ownerNwtId', 'ownerUid', 'ownerName', 'nwtId', 'deptId', 'siteId'];

/**
 * Edit a TRL the submitter owns — allowed ONLY while still 'submitted'
 * (i.e. before the FLS has started review). Throws otherwise.
 */
export async function updateTRLBySubmitter(trlId, fields = {}, { submittedBy } = {}) {
  const ref = doc(db, TRL_COL, trlId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Submission not found.');
  const data = snap.data();
  if (submittedBy && data.submittedBy !== submittedBy) {
    throw new Error('You can only edit your own submissions.');
  }
  if (data.status !== 'submitted') {
    throw new Error('This submission is already under review and can no longer be edited.');
  }
  const updates = {};
  ITM_EDITABLE_FIELDS.forEach((k) => {
    if (fields[k] !== undefined) updates[k] = fields[k];
  });
  if (updates.type) {
    updates.kind = conditionKindOf(updates.type);
    if (updates.type !== 'drift') updates.driftType = null;
    if (updates.type !== 'trap') updates.trapCategory = null;
  }
  await updateDoc(ref, {
    ...updates,
    updatedAt: Timestamp.now(),
    updatedBy: submittedBy || data.submittedBy,
  });
}

/**
 * Withdraw a condition the submitter owns — allowed ONLY while still 'submitted' (Pending
 * Review). Soft delete (⚑Q-13 adopted 09-12): `status: 'withdrawn'`, hidden from queues and
 * dashboards, kept for the audit trail Tim wants ("records must be maintained").
 */
export async function withdrawTRL(trlId, { submittedBy, submittedByName, actingFor } = {}) {
  const ref = doc(db, TRL_COL, trlId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return; // already gone — treat as success
  const data = snap.data();
  if (submittedBy && data.submittedBy !== submittedBy) {
    throw new Error('You can only withdraw your own submissions.');
  }
  if (data.status !== 'submitted') {
    throw new Error('This submission is already under review and can no longer be withdrawn.');
  }
  await updateDoc(ref, {
    status: 'withdrawn',
    previousStatus: data.status,
    withdrawnAt: Timestamp.now(),
    withdrawnBy: submittedBy || data.submittedBy,
    statusHistory: [...(Array.isArray(data.statusHistory) ? data.statusHistory : []), historyEntry('withdrawn', { by: submittedBy, byName: submittedByName, actingFor })],
    updatedAt: Timestamp.now(),
    updatedBy: submittedBy || data.submittedBy,
  });
  logConditionActivity('condition_withdrawn', { id: trlId, ...data, status: 'withdrawn' }, { by: submittedBy, actingFor });
}

/** Hard delete — administrators only (rules); ordinary users withdraw (soft) instead. */
export async function deleteTRLPermanently(trlId) {
  await deleteDoc(doc(db, TRL_COL, trlId));
}

/* ------------------------------------------------------------------------ */
/* Resolve / reopen — ticket-style "fixed it" loop                           */
/* ------------------------------------------------------------------------ */

/**
 * Mark a TRL as fixed/resolved. Closes the loop for everyone in the chain —
 * because it's one shared record, the ITM, FLS, and DH views all reflect it.
 * Allowed from any active state (submitted / under_review / approved); a
 * rejected item can't be "fixed".
 */
export async function resolveTRL(trlId, { resolvedBy, resolvedByName, note } = {}) {
  const ref = doc(db, TRL_COL, trlId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('TRL not found.');
  const data = snap.data();
  if (data.status === 'resolved') return; // already fixed
  if (data.status === 'rejected') throw new Error('This entry was rejected and cannot be marked fixed.');
  await updateDoc(ref, {
    status: 'resolved',
    previousStatus: data.status,
    resolvedBy: resolvedBy || null,
    resolvedByName: resolvedByName || '',
    resolvedAt: Timestamp.now(),
    resolutionNote: note || '',
    // "Found and fixed" straight from Pending Review — future hero badges / HUGO feed (P2-31)
    selfResolved: data.status === 'submitted',
    resolution: { ...(data.resolution || {}), submittedAt: Timestamp.now(), submittedBy: resolvedBy || null, submittedByName: resolvedByName || '', note: note || '', actions: data.resolution?.actions || [] },
    statusHistory: [...(Array.isArray(data.statusHistory) ? data.statusHistory : []), historyEntry('resolved', { by: resolvedBy, byName: resolvedByName, note })],
    updatedAt: Timestamp.now(),
    updatedBy: resolvedBy || data.updatedBy || null,
  });
  logConditionActivity('condition_resolved', { id: trlId, ...data, status: 'resolved' }, { by: resolvedBy });
}

/**
 * Reopen a resolved TRL back to the state it was in before it was fixed (undo).
 */
export async function reopenTRL(trlId, { reopenedBy } = {}) {
  const ref = doc(db, TRL_COL, trlId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('TRL not found.');
  const data = snap.data();
  if (data.status !== 'resolved') return;
  await updateDoc(ref, {
    status: data.previousStatus || 'submitted',
    resolvedBy: null,
    resolvedByName: '',
    resolvedAt: null,
    resolutionNote: '',
    updatedAt: Timestamp.now(),
    updatedBy: reopenedBy || data.updatedBy || null,
  });
}

/* ------------------------------------------------------------------------ */
/* Read — scoped queries the role pages use                                  */
/* ------------------------------------------------------------------------ */

/**
 * Build Firestore where() constraints from a queryScope shape.
 * queryScope is supplied by DemoRoleContext (or by the real auth/role system in production).
 *
 * Scoping rules — broadest match wins (i.e. an ITM scope filters more tightly than a Site scope):
 *   - ITM:  filter by submittedBy = userId   (only their own submissions)
 *   - FLS:  filter by nwtId
 *   - DH:   filter by deptId
 *   - Site: filter by siteId
 *   - Exec: filter by organizationId
 *   - Admin / PPI: no filter (sees everything)
 */
function buildScopeConstraints(scope = {}) {
  const constraints = [];
  const { personaId, userId, nwtId, deptId, siteId, organizationId } = scope;

  // Most specific first — ITMs only see their own
  if (personaId === 'itm' && userId) {
    constraints.push(where('submittedBy', '==', userId));
  } else if (personaId === 'fls' && nwtId) {
    constraints.push(where('nwtId', '==', nwtId));
  } else if (personaId === 'dh' && deptId) {
    constraints.push(where('deptId', '==', deptId));
  } else if ((personaId === 'site_manager' || personaId === 'executive') && organizationId) {
    if (siteId && personaId === 'site_manager') {
      constraints.push(where('siteId', '==', siteId));
    } else {
      constraints.push(where('organizationId', '==', organizationId));
    }
  } else if (organizationId) {
    // Everyone else — INCLUDING org admins — is fenced to their organization.
    // (Pre-beta, admins had no constraint and saw every org's TRLs. Fine when
    // the only admins were superadmins; a cross-tenant data leak once every
    // beta tester became org_admin of their own Magnum copy.)
    constraints.push(where('organizationId', '==', organizationId));
  }
  // Only a scope with no organizationId at all sees everything (global
  // superadmin context with no active org).

  return constraints;
}

/**
 * Fetch TRLs scoped to a queryScope, optionally filtered by status.
 */
export async function listTRLs(scope = {}, { status, limit = 50 } = {}) {
  const ref = collection(db, TRL_COL);
  const constraints = buildScopeConstraints(scope);
  if (status) constraints.push(where('status', '==', status));
  constraints.push(orderBy('submittedAt', 'desc'));
  if (limit) constraints.push(firestoreLimit(limit));
  const q = query(ref, ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to TRLs for a scope. Returns an unsubscribe function.
 */
export function subscribeToTRLs(scope, callback, { status, limit = 100, onError } = {}) {
  const ref = collection(db, TRL_COL);
  const constraints = buildScopeConstraints(scope);
  if (status) constraints.push(where('status', '==', status));
  constraints.push(orderBy('submittedAt', 'desc'));
  if (limit) constraints.push(firestoreLimit(limit));
  const q = query(ref, ...constraints);
  return onSnapshot(
    q,
    (snapshot) => {
      callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      // Surface failures (e.g. a missing composite index) instead of silently
      // leaving the list empty — that looks like "my entry disappeared".
      console.error('subscribeToTRLs query failed:', err);
      if (typeof onError === 'function') onError(err);
    }
  );
}

/**
 * Fetch a single TRL by ID.
 */
export async function getTRL(trlId) {
  const ref = doc(db, TRL_COL, trlId);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/* ------------------------------------------------------------------------ */
/* Aggregate stats (for dashboard headers and rollup pages)                  */
/* ------------------------------------------------------------------------ */

/**
 * Compute stats from a TRL list. Used by ITM/FLS/DH/Site dashboards to show
 * counts without firing a separate aggregate query.
 */
export function computeTRLStats(trls) {
  const stats = {
    total: 0, // withdrawn / cancelled excluded (hidden from queues and dashboards)
    submitted: 0,
    underReview: 0,
    approved: 0,
    rejected: 0,
    severityIV: 0,
    p1: 0,
    refActionsTotal: 0,
    byQuadrant: { reliability: 0, efficiency: 0, productivity: 0, safety: 0 },
    byType: { drift: 0, trap: 0, roadblock: 0, landmine: 0 },
    byKind: { drift: 0, trl: 0 },
    reviewL1: 0,
    resolutionPending: 0,
    resolved: 0,
    open: 0,
  };
  for (const t of trls) {
    if (t.status === 'withdrawn' || t.status === 'cancelled') continue; // hidden from queues/dashboards
    stats.total += 1;
    stats.byKind[t.kind || conditionKindOf(t.type)] += 1;
    if (['submitted', 'review_l1', 'under_review', 'approved', 'resolution_pending'].includes(t.status)) stats.open++;
    if (t.status === 'review_l1') stats.reviewL1++;
    if (t.status === 'resolution_pending') stats.resolutionPending++;
    if (t.status === 'resolved') stats.resolved++;
    if (t.status === 'submitted') stats.submitted++;
    else if (t.status === 'under_review') stats.underReview++;
    else if (t.status === 'approved') stats.approved++;
    else if (t.status === 'rejected') stats.rejected++;

    const finalSev = t.severityDH || t.severityFLS || t.severityITM;
    if (finalSev === 'IV') stats.severityIV++;
    if (t.priority === 'P1') stats.p1++;

    stats.refActionsTotal += (t.refActions?.length || 0);
    if (t.repsQuadrant && stats.byQuadrant[t.repsQuadrant] !== undefined) {
      stats.byQuadrant[t.repsQuadrant]++;
    }
    if (t.type && stats.byType[t.type] !== undefined) {
      stats.byType[t.type]++;
    }
  }
  return stats;
}
